const DEFAULT_CUSTOMIZABLE_TOKENS = ['5a', '4a']; // grade codes only; never add 'grade'/'select'/'value' (would match Value Grade-3A)
const FALLBACK_MESSAGE_VARIANT_UNFIT = "The variant you customized isn't available at the required quantity. We've selected the next best option.";
const FALLBACK_MESSAGE_NO_VARIANT_INFO = "We couldn't identify the variant you customized. We've selected the best available option.";
const NO_ELIGIBLE_VARIANT_MESSAGE = "No customizable variant is currently available at the required quantity. Please contact us.";
const MIN_CUSTOMIZATION_QTY = 4;

// Hidden line-item property recording that the customer acknowledged a lower-condition
// grade on the Qstomizer return page.
const CONDITION_ACK_PROPERTY = '_condition_acknowledged';

// We stash the user's selection (variant + qty) at Customize-click time under one key.
// Qstomizer's return `variant=` param reflects the theme default, not our custom picker,
// so our own stash is the only reliable record of what the customer actually chose.
const STASH_KEY = 'qstomizerReturn:selection';
// Guard against a stale stash from an abandoned round-trip poisoning the next attempt.
const STASH_TTL_MS = 60 * 60 * 1000; // 1 hour

// Caption shown under the Qstomizer "Customize" button. Previously a CSS ::after
// pseudo-element, but a Qstomizer stylesheet update started overriding `content`,
// so we inject a real element we control instead. One caption per button state.
const CUSTOMIZE_CAPTION_CLASS = 'qstomizer-product-button-caption';
const CAPTION_DEFAULT = 'Only 20 cents per ball!';
const CAPTION_DISABLED = 'Unlock with 4 dozen or more 5A or 4A balls!';
const CAPTION_UNAVAILABLE = 'Not currently available in selected quantity';

class ProductVariantBulkNew extends HTMLElement {
    constructor() {
        super();
        this.quantityInputs = Array.from(this.querySelectorAll('input[name="quantity"]'));
        this.variantInputs = Array.from(this.querySelectorAll('input[name="id"]'));

        this.form = this.querySelector('form');
        this.fallbackMessageEl = this.querySelector('.customization-fallback-message');

        this.customizableTokens = this.parseCustomizableTokens();
        this.conditionTokens = this.parseConditionTokens();
        this.conditionNoticeEl = this.querySelector('.condition-notice__body');
        // Variant the acknowledgment checkbox was last shown for — a change resets the box.
        this.conditionAckVariantId = null;
        this.isCustomizationReturn = false;
        // True when this page load is a return from Qstomizer (URL carries `imf`).
        // On the return page we must NOT overwrite the stash — we consume it.
        this.isReturnPage = new URLSearchParams(window.location.search).has('imf');

        this.init = this.init.bind(this);
        this.handleFormChanges = this.handleFormChanges.bind(this);
        this.handleConditionAckChange = this.handleConditionAckChange.bind(this);
    }

    // An empty setting intentionally turns the feature off (no notice, no checkbox) —
    // unlike customizable_variant_tokens, there is no default fallback.
    parseConditionTokens() {
        return (this.dataset.conditionTokens || '')
            .split(',')
            .map(t => t.trim().toLowerCase())
            .filter(Boolean);
    }

    matchesConditionToken(variantName) {
        if (!variantName || !this.conditionTokens.length) return false;
        const name = String(variantName).toLowerCase();
        return this.conditionTokens.some(token => name.includes(token));
    }

    // A lower-condition grade is a customizable variant whose name matches a condition token.
    isConditionGrade(input) {
        const variantName = input?.dataset?.name;
        return this.isVariantCustomizable(variantName) && this.matchesConditionToken(variantName);
    }

    // Catch a mistyped setting (e.g. "nearmint"). Only in-stock variants carry data-name, so
    // this also fires on a product with no lower grade in stock — expected there.
    warnIfNoConditionVariantsMatch() {
        if (!this.conditionTokens.length) return;
        const anyMatch = this.variantInputs.some(input => this.isConditionGrade(input));
        if (!anyMatch) {
            console.warn('[product-variant-bulk-new] no in-stock customizable variant matches condition_notice_variant_tokens — check the theme setting if this product has a lower grade');
        }
    }

    // Part A — inline notice above the Customize button, shown for a lower-condition grade
    // once the selected quantity meets the customization minimum.
    updateConditionNotice() {
        if (!this.conditionNoticeEl) return;
        const selectedVariant = this.variantInputs.find(input => input.checked);
        const selectedQty = Number(this.quantityInputs.find(input => input.checked)?.value || 0);
        this.conditionNoticeEl.hidden = !this.isConditionGrade(selectedVariant)
            || selectedQty < MIN_CUSTOMIZATION_QTY;
    }

    // Links in the merchant-edited notice (e.g. grading info) open in a new tab so the
    // customer doesn't lose their selection.
    openConditionNoticeLinksInNewTab() {
        this.conditionNoticeEl?.querySelectorAll('a[href]').forEach(link => {
            link.target = '_blank';
            link.rel = 'noopener';
        });
    }

    // The checkbox only applies on the Qstomizer return page, for a lower-condition grade,
    // and only if the buy_button_custom block rendered the checkbox markup.
    isConditionAckRequired() {
        if (!this.isCustomizationReturn || !this.conditionAck || !this.conditionAckInput) return false;
        return this.isConditionGrade(this.variantInputs.find(input => input.checked));
    }

    // Part B — show/hide the acknowledgment checkbox and gate Add to Cart on it.
    updateConditionGate() {
        if (!this.conditionAck || !this.conditionAckInput) return;

        const required = this.isConditionAckRequired();
        const selectedVariant = this.variantInputs.find(input => input.checked);
        const selectedId = selectedVariant ? selectedVariant.value : null;

        // Acknowledgment is per grade: switching variant always resets the box.
        if (selectedId !== this.conditionAckVariantId) {
            this.conditionAckInput.checked = false;
            this.conditionAckVariantId = selectedId;
        }

        const acknowledged = this.conditionAckInput.checked;
        this.conditionAck.hidden = !required;
        if (this.conditionAckHint) this.conditionAckHint.hidden = !required || acknowledged;

        const button = this.conditionAckButton;
        if (!button) return;
        if (required) {
            button.disabled = !acknowledged;
            if (this.conditionAckHint) button.setAttribute('aria-describedby', this.conditionAckHint.id);
        } else {
            button.disabled = this.conditionAckButtonInitiallyDisabled;
            button.removeAttribute('aria-describedby');
        }
    }

    handleConditionAckChange() {
        this.updateConditionGate();
    }

    parseCustomizableTokens() {
        const raw = this.dataset.customizableTokens || '';
        const tokens = raw
            .split(',')
            .map(t => t.trim().toLowerCase())
            .filter(Boolean);

        if (!tokens.length) {
            console.warn('[product-variant-bulk-new] customizable_variant_tokens setting is empty; using defaults');
            return DEFAULT_CUSTOMIZABLE_TOKENS.slice();
        }
        return tokens;
    }

    isVariantCustomizable(variantName) {
        if (!variantName || !this.customizableTokens.length) return false;
        const name = String(variantName).toLowerCase();
        return this.customizableTokens.some(token => name.includes(token));
    }

    warnIfNoVariantsMatch() {
        const anyMatch = this.variantInputs.some(input => this.isVariantCustomizable(input.dataset?.name));
        if (!anyMatch) {
            console.warn('[product-variant-bulk-new] no variants on this product match any customizable token — check theme setting');
        }
    }

    // A variant is a viable customization target only if it's a customizable grade AND
    // actually has customizable inventory to draw from. A customizable grade with a blank/
    // zero custom_inventory_level (e.g. 5A here) is NOT eligible.
    isVariantEligible(input) {
        return this.isVariantCustomizable(input?.dataset?.name)
            && Number(input?.dataset?.customInventory || 0) >= MIN_CUSTOMIZATION_QTY;
    }

    findBestEligibleVariant() {
        const candidates = this.variantInputs.filter(input => this.isVariantEligible(input));
        candidates.sort((a, b) =>
            Number(b.dataset.customInventory || 0) - Number(a.dataset.customInventory || 0)
        );
        return candidates[0] || null;
    }

    showFallbackMessage(text) {
        if (!this.fallbackMessageEl) return;
        this.fallbackMessageEl.textContent = text;
        this.fallbackMessageEl.hidden = false;
    }

    // Select a variant radio and notify the rest of the component (prices, qty gating).
    selectVariant(input) {
        this.variantInputs.forEach(i => { i.checked = false; });
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Mirror the currently-selected variant + quantity to storage. Called on every user
    // change (and initial load), so the real choice is saved before the Qstomizer round-trip.
    // Stored together so variant and qty can't drift apart.
    stashSelection() {
        const selectedVariant = this.variantInputs.find(input => input.checked);
        const selectedQty = this.quantityInputs.find(input => input.checked);
        if (!selectedVariant && !selectedQty) return;
        const payload = {
            variantId: selectedVariant ? selectedVariant.value : null,
            qty: selectedQty ? selectedQty.value : null,
            ts: Date.now()
        };
        // localStorage (not sessionStorage): the Qstomizer round-trip may land in a
        // different tab, and sessionStorage is per-tab. Controlled lifetime is handled
        // explicitly below (consume-on-read + TTL).
        try {
            localStorage.setItem(STASH_KEY, JSON.stringify(payload));
        } catch (e) {
            // localStorage may be unavailable (privacy mode, quota); silently skip.
        }
    }

    // Read the stash WITHOUT consuming it, so re-visiting the same return URL (browser
    // back, refresh, or the "return to previous page" button after clearing the cart)
    // restores the same selection instead of falling back to the unreliable URL param.
    // Staleness is bounded by STASH_TTL_MS and by the fact that any selection change on a
    // product page overwrites it (see stashSelection) — so consuming here is unnecessary
    // and would break idempotent re-visits.
    readStash() {
        let raw = null;
        try {
            raw = localStorage.getItem(STASH_KEY);
        } catch (e) {
            return null;
        }
        if (!raw) return null;

        let data;
        try {
            data = JSON.parse(raw);
        } catch (e) {
            return null;
        }
        if (!data || typeof data !== 'object') return null;
        if (!data.ts || (Date.now() - data.ts) > STASH_TTL_MS) return null;
        return data;
    }

    restoreStashedQuantity(stashedQty) {
        if (!stashedQty) return;

        // Prefer the stashed qty if still valid; otherwise fall back to the highest enabled qty.
        const stashedTarget = this.quantityInputs.find(input => input.value === stashedQty);
        let qtyToSelect = (stashedTarget && !stashedTarget.disabled) ? stashedTarget : null;
        if (!qtyToSelect) {
            qtyToSelect = this.quantityInputs
                .filter(input => !input.disabled)
                .sort((a, b) => Number(b.value) - Number(a.value))[0] || null;
        }
        if (!qtyToSelect) return;

        this.quantityInputs.forEach(input => {
            input.checked = false;
            input.closest('.variations-js')?.classList.remove('selected');
        });
        qtyToSelect.checked = true;
        qtyToSelect.closest('.variations-js')?.classList.add('selected');
        qtyToSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    connectedCallback() {
        this.init();
    }

    disconnectedCallback() {
        this.conditionAckInput?.removeEventListener('change', this.handleConditionAckChange);
    }

    init() {
        this.updatePrices();
        this.warnIfNoVariantsMatch();

        this.addEventListener('change', this.handleFormChanges);

        // The acknowledgment checkbox and Add to Cart live in the separate buy_button_custom
        // block (outside this element), so they're looked up on the document.
        this.conditionAck = document.querySelector('[data-condition-ack]');
        this.conditionAckInput = this.conditionAck?.querySelector('[data-condition-ack-input]') || null;
        this.conditionAckHint = this.conditionAck?.querySelector('[data-condition-ack-hint]') || null;
        this.conditionAckButton = document.querySelector('[data-condition-ack-button]');
        // Remember the Liquid-rendered state so un-gating never re-enables a disabled button.
        this.conditionAckButtonInitiallyDisabled = this.conditionAckButton ? this.conditionAckButton.disabled : false;
        this.conditionAckInput?.addEventListener('change', this.handleConditionAckChange);
        this.warnIfNoConditionVariantsMatch();
        this.openConditionNoticeLinksInNewTab();
        this.updateConditionNotice();

        // Continuously mirror the user's selection to storage on every change (see
        // stashSelection, wired into handleFormChanges + initial load). We do NOT try to
        // intercept the Qstomizer "Customize" click — that button doesn't surface a
        // catchable click to our page. Because we stash on every change, whatever the user
        // last selected is already saved before they ever click Customize.

        window.addEventListener('load', () => {
            // Capture the incoming stash BEFORE any programmatic change can fire and
            // overwrite it. handleCustomizedProduct reads this to restore the selection.
            this.incomingStash = this.isReturnPage ? this.readStash() : null;

            let qtyInputChecked = this.quantityInputs.find(input => input.checked);

            if (qtyInputChecked) {
                qtyInputChecked.closest('.variations-js').classList.add('selected');
            } else {
                const defaultQtyInput = this.quantityInputs.find(input => input.value === '4');
                defaultQtyInput.checked = true;
                defaultQtyInput.closest('.variations-js').classList.add('selected');
            }

            this.updateQuantityAvailability();
            this.handleCustomizeBtnState();

            // Save the initial selection so a customer who changes nothing before clicking
            // Customize still has their (default) pick preserved. Skipped on the return page,
            // where the selection is restored from the stash we captured above (writing the
            // pre-restore default here would briefly clobber it).
            if (!this.isReturnPage) this.stashSelection();

            this.handleCustomizedProduct();

            // Covers return-page paths where no change event fired during setup.
            this.updateConditionNotice();
            this.updateConditionGate();
        });
    }

    handleFormChanges(event) {
        const { name } = event.target;
        if (name !== 'quantity' && name !== 'id') return;

        if (name === 'quantity') {
            this.quantityInputs.forEach(input => {
                input.closest('.variations-js').classList.toggle('selected', input === event.target);
            });
        }

        if (name === 'id') {
            this.updateQuantityAvailability();
            this.updatePrices();
            this.updateConditionGate();
        }

        // Grade and quantity both affect the notice.
        this.updateConditionNotice();
        this.handleCustomizeBtnState();

        // Mirror the current selection to storage on every change — including changes the
        // customer makes on the return page (e.g. bumping the quantity), so a later revisit
        // restores what they last chose. Safe on the return page because the pre-existing
        // stash was already captured (this.incomingStash) before any change could fire.
        this.stashSelection();
    }

    handleCustomizeBtnState() {
        const customizeBtn = document.querySelector('#qstomizer-product-button');
        if (!customizeBtn) return;

        // Optional chaining: on a sold-out product no variant radio is checked.
        const selectedQty = this.quantityInputs.find(input => input.checked)?.value;
        const currentCustomInventory = this.getCurrentVariantCustomInventory();
        const selectedVariantName = this.variantInputs.find(input => input.checked)?.dataset?.name;
        const isCustomizableVariant = this.isVariantCustomizable(selectedVariantName);

        const isDisabled = selectedQty < MIN_CUSTOMIZATION_QTY || !isCustomizableVariant;
        const customIsAvailable = ((currentCustomInventory >= selectedQty) && isCustomizableVariant);

        let caption;
        if (isDisabled) {
            customizeBtn.classList.add('is-disabled');
            customizeBtn.classList.remove('not-available');
            caption = CAPTION_DISABLED;
        } else if (!customIsAvailable) {
            customizeBtn.classList.add('not-available');
            customizeBtn.classList.remove('is-disabled');
            caption = CAPTION_UNAVAILABLE;
        } else {
            customizeBtn.classList.remove('is-disabled', 'not-available');
            caption = CAPTION_DEFAULT;
        }

        this.renderCustomizeCaption(customizeBtn, caption);
    }

    // Inject/update the caption as a real child element. Idempotent: if Qstomizer
    // re-renders and replaces the button, the next call rebuilds it on the new node.
    renderCustomizeCaption(button, text) {
        let caption = button.querySelector(`.${CUSTOMIZE_CAPTION_CLASS}`);
        if (!caption) {
            caption = document.createElement('span');
            caption.className = CUSTOMIZE_CAPTION_CLASS;
            button.appendChild(caption);
        }
        if (caption.textContent !== text) {
            caption.textContent = text;
        }
    }

    // DEPRECATED DEALEASY/SHOPIFY(?) DISCOUNT FORMULA
    // updatePrices() {
    //     const selectedVariantInput = this.variantInputs.find(input => input.checked);
    //     const unitCents = Math.round(Number(selectedVariantInput?.dataset?.price));

    //     this.quantityInputs.forEach(input => {
    //         const wrapper = input.closest('.variations-js');
    //         const priceContainer = wrapper?.querySelector('.price-js');
    //         const fullPriceContainer = wrapper?.querySelector('.full-price-js');
    //         const discountContainer = wrapper?.querySelector('.discount-js');

    //         const discount = Number(input?.dataset?.discount) || 0;
    //         const units = Math.round(Number(input?.dataset?.units)) || 1;

    //         const unitDiscountCents = unitCents * (discount / 100);
    //         const discountedUnitCents = Math.ceil(unitCents - unitDiscountCents);

    //         const subtotalCents = (unitCents * units);
    //         const finalCents = discountedUnitCents * units;
    //         const totalDiscountCents = subtotalCents - finalCents;

    //         if (priceContainer && !Number.isNaN(finalCents)) {
    //             priceContainer.innerText = `$${(finalCents / 100).toFixed(2)}`;
    //         }
    //         if (fullPriceContainer && !Number.isNaN(subtotalCents)) {
    //             fullPriceContainer.innerText = `$${(subtotalCents / 100).toFixed(2)}`;
    //         }
    //         if (discountContainer && !Number.isNaN(totalDiscountCents)) {
    //             discountContainer.innerText = `$${(totalDiscountCents / 100).toFixed(2)}`;
    //         }
    //     });
    // }

    // NEW DEALEASY/SHOPIFY(?) DISCOUNT FORMULA
    updatePrices() {
        const selectedVariantInput = this.variantInputs.find(input => input.checked);
        const unitCents = Math.round(Number(selectedVariantInput?.dataset?.price));

        this.quantityInputs.forEach(input => {
            const wrapper = input.closest('.variations-js');
            const priceContainer = wrapper?.querySelector('.price-js');
            const fullPriceContainer = wrapper?.querySelector('.full-price-js');
            const discountContainer = wrapper?.querySelector('.discount-js');

            const discount = Number(input?.dataset?.discount) || 0;
            const units = Math.round(Number(input?.dataset?.units)) || 1;

            const unitDiscountCents = unitCents * (discount / 100);
            const discountedUnitCents = Math.ceil(unitCents - unitDiscountCents);

            const subtotalCents = (unitCents * units);
            const finalCents = discountedUnitCents * units;
            const totalDiscountCents = subtotalCents - finalCents;

            if (priceContainer && !Number.isNaN(finalCents)) {
                priceContainer.innerText = `$${(finalCents / 100).toFixed(2)}`;
            }
            if (fullPriceContainer && !Number.isNaN(subtotalCents)) {
                fullPriceContainer.innerText = `$${(subtotalCents / 100).toFixed(2)}`;
            }
            if (discountContainer && !Number.isNaN(totalDiscountCents)) {
                discountContainer.innerText = `$${(totalDiscountCents / 100).toFixed(2)}`;
            }

            this.updateShippingMessaging(input, finalCents);
        });
    }

    updateShippingMessaging(input, rawPrice) {
        const freeShippingThreshold = Number(this.dataset.freeShippingThreshold) || null;
        const shippingMessagingEl = input.closest('.variations-js').querySelector('.shipping-messaging-js');

        if (!shippingMessagingEl) return;

        const noFreeShipping = !freeShippingThreshold || rawPrice < freeShippingThreshold;
        shippingMessagingEl.classList.toggle('unavailable-js', noFreeShipping);
    }

    getCurrentVariantInventory() {
        const variant = this.variantInputs.find(input => input.checked);
        return Number(variant?.dataset?.inventory || 0);
    }

    getCurrentVariantCustomInventory() {
        const variant = this.variantInputs.find(input => input.checked);
        return Number(variant?.dataset?.customInventory || 0);
    }

    updateQuantityAvailability() {
        const qtyAvailable = this.getCurrentVariantInventory();
        const customInventory = this.isCustomizationReturn ? this.getCurrentVariantCustomInventory() : null;

        this.quantityInputs.forEach(input => {
            const qty = Number(input.value);
            let disabled = qty > qtyAvailable;

            if (this.isCustomizationReturn) {
                if (qty < MIN_CUSTOMIZATION_QTY) disabled = true;
                if (customInventory !== null && qty > customInventory) disabled = true;
            }

            input.disabled = disabled;
            input.closest('.variations-js')?.classList.toggle('is-disabled', disabled);
        });

        // Auto-fallback in case selected qty is now invalid
        const invalid = this.quantityInputs.find(input => input.checked && input.disabled);

        if (invalid) {
            const fallback = this.quantityInputs
                .filter(input => !input.disabled)
                .sort((a, b) => Number(b.value) - Number(a.value))[0];
            
            if (fallback) {
                fallback.checked = true;
                fallback.dispatchEvent(
                    new Event('change', { bubbles: true })
                );
            }
        }
    }

    handleCustomizedProduct() {
        const url = new URL(window.location.href);
        if (!url) return;

        const qstomizerParam = url.searchParams.get('imf');
        if (!qstomizerParam) return;

        // Enter customization-return mode — stricter qty gating persists across variant changes.
        this.isCustomizationReturn = true;

        const qstomizerContainer = this.querySelector('.qsmzimagesContainer');
        if (qstomizerContainer) {
            this.append(qstomizerContainer);
        }

        // Our own stash is the authoritative record of what the customer picked —
        // Qstomizer's return `variant=` param reflects the theme default, not our custom
        // picker. Prefer the stashed variant; fall back to the URL param only if the stash
        // is missing/expired (e.g. different tab, timed out). `incomingStash` was captured
        // at the very top of the load handler, before any change event could overwrite it.
        const stash = this.incomingStash;
        const variantIdFromUrl = url.searchParams.get('variant');
        const targetVariantId = (stash && stash.variantId) || variantIdFromUrl;

        const targetVariantInput = targetVariantId
            ? this.variantInputs.find(input => input.value === targetVariantId)
            : null;

        const targetIsViable = targetVariantInput && this.isVariantEligible(targetVariantInput);

        if (targetIsViable) {
            this.selectVariant(targetVariantInput);
        } else {
            const fallback = this.findBestEligibleVariant();
            if (fallback) {
                this.selectVariant(fallback);
                // Differentiate the message: did we have a variant hint we couldn't honor,
                // or did we have nothing to go on at all?
                this.showFallbackMessage(
                    targetVariantId ? FALLBACK_MESSAGE_VARIANT_UNFIT : FALLBACK_MESSAGE_NO_VARIANT_INFO
                );
            } else {
                this.showFallbackMessage(NO_ELIGIBLE_VARIANT_MESSAGE);
            }
        }

        // Disable every variant that isn't a viable customization target — not only the
        // non-customizable grades (e.g. 3A), but also customizable grades with no
        // customizable inventory (e.g. a 5A whose custom_inventory_level is blank). In
        // return mode the customer may only pick a variant they can actually customize.
        this.variantInputs.forEach(input => {
            if (!this.isVariantEligible(input)) {
                input.disabled = true;
                input.closest('.variant-block')?.classList.add('disabled-item');
            }
        });

        // Ensure return-mode qty guards are applied even when no change event fired
        // (e.g., no eligible variant found, nothing to preselect).
        this.updateQuantityAvailability();

        // Restore the user's originally-selected quantity from the same stash, if still valid.
        this.restoreStashedQuantity(stash ? stash.qty : null);

        this.form.addEventListener('submit', (e) => {
            e.preventDefault();

            // The Add to Cart block calls form.requestSubmit() directly, so this is the real
            // gate — the disabled button is only the visual cue.
            const conditionAckRequired = this.isConditionAckRequired();
            if (conditionAckRequired && !this.conditionAckInput.checked) {
                this.updateConditionGate();
                this.conditionAckInput.focus();
                return;
            }

            const formData = new FormData(this.form);
            const mainVariantId = formData.get('id');
            const mainQty = Number(formData.get('quantity'));
           
            const properties = {};
            for (const [key, value] of formData.entries()) {
                if (key !== 'id' && key !== 'quantity') {
                    const cleanKey = key.replace(/^properties\[(.+)\]$/, '$1');
                    properties[`${cleanKey}`] = value;
                }
            }
            properties['_customized'] = 'Yes';
            if (conditionAckRequired) {
                properties[CONDITION_ACK_PROPERTY] = 'Yes';
            }

            const payload = {
                items: [
                    {
                        id: mainVariantId,
                        quantity: mainQty,
                        ...(Object.keys(properties).length > 0 && { properties })
                    },
                    {
                        id: 43059913949289,
                        quantity: mainQty,
                        properties: {
                            _customization_fee_for: mainVariantId
                        }
                    }
                ]
            }

            fetch(window.Shopify.routes.root + 'cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            })
            .then(response => {
                return response.json();
            })
            .then(data => {
                console.log(data);
                window.location.href = '/cart';
            })
            .catch(error => {
                console.error('Error: ', error);
            });
        })
    }

}

customElements.define('product-variant-bulk-new', ProductVariantBulkNew);